import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useNavigate } from "react-router-dom";
import { messaging, getToken, deleteToken, db } from "../firebase";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import useQuickActionPrefs, {
  ALL_QUICK_ACTIONS,
} from "../hooks/useQuickActionPrefs";
import { useChild } from "../context/ChildContext";
import { useThemeMode } from "../context/ThemeContext";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { currentChild, childList } = useChild();
  const { user } = useAuth();
  const { visibleActions, toggleAction } = useQuickActionPrefs();
  const { t } = useTranslation();
  const { mode, toggleMode } = useThemeMode();
  const [language, setLanguage] = useState(localStorage.getItem("language") || "da");
  const [gender, setGender] = useState(currentChild?.gender ?? "boy");

  const ACTION_META = {
    feeding: { icon: "🍼", label: t("settings.actions.feeding") },
    sleep: { icon: "😴", label: t("settings.actions.sleep") },
    tummyTime: { icon: "🧸", label: t("settings.actions.tummyTime") },
    diaper: { icon: "💩", label: t("settings.actions.diaper") },
    extractedMilk: { icon: "🥛", label: t("settings.actions.extractedMilk") },
    medicine: { icon: "💊", label: "Medicin" },
  };

  const handleLanguageChange = (_, newLang) => {
    if (!newLang) return;
    setLanguage(newLang);
    localStorage.setItem("language", newLang);
    i18n.changeLanguage(newLang);
  };

  const [hasToken, setHasToken] = useState(false);
  const [notificationChildren, setNotificationChildren] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.data() ?? {};
        setHasToken(!!data.fcmToken);
        setNotificationChildren(data.notificationChildren ?? []);
        if (Notification.permission === "granted" && data.fcmToken) {
          const token = await getToken(messaging, { vapidKey: VAPID_KEY });
          if (token) {
            await updateDoc(doc(db, "users", user.uid), {
              fcmToken: token,
              fcmTokenUpdatedAt: new Date().toISOString(),
            });
          }
        }
      } catch (err) {
        console.error("Token refresh error:", err);
      }
    };
    load();
  }, [user]);

  const handleToggleNotifications = async () => {
    setNotifLoading(true);
    try {
      if (hasToken) {
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (token) await deleteToken(messaging);
        await updateDoc(doc(db, "users", user.uid), {
          fcmToken: null,
          fcmTokenUpdatedAt: null,
        });
        setHasToken(false);
      } else {
        const result = await Notification.requestPermission();
        if (result === "granted") {
          const token = await getToken(messaging, { vapidKey: VAPID_KEY });
          if (token) {
            const defaultChildren = childList.map((c) => c.id);
            await updateDoc(doc(db, "users", user.uid), {
              fcmToken: token,
              fcmTokenUpdatedAt: new Date().toISOString(),
              notificationChildren:
                notificationChildren.length > 0
                  ? notificationChildren
                  : defaultChildren,
            });
            if (notificationChildren.length === 0)
              setNotificationChildren(defaultChildren);
            setHasToken(true);
          }
        }
      }
    } catch (err) {
      console.error("Notification toggle error:", err);
    }
    setNotifLoading(false);
  };

  const handleToggleChild = async (childId) => {
    const updated = notificationChildren.includes(childId)
      ? notificationChildren.filter((id) => id !== childId)
      : [...notificationChildren, childId];
    setNotificationChildren(updated);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        notificationChildren: updated,
      });
    } catch (err) {
      console.error("Error updating notification children:", err);
    }
  };

  const handleGenderChange = async (_, newGender) => {
    if (!newGender || !currentChild?.id) return;
    setGender(newGender);
    try {
      await updateDoc(doc(db, "children", currentChild.id), { gender: newGender });
    } catch (err) {
      console.error("Error updating gender:", err);
    }
  };

  const handleBack = () => {
    if (currentChild?.id) {
      navigate(`/tracker/${currentChild.id}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.page", pt: 8 }}>
      <Container maxWidth="sm">
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}>
          <Tooltip title={t("nav.back")}>
            <IconButton onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h5"
            fontWeight={600}
          >
            {t("settings.title")}
          </Typography>
        </Box>

        <Paper
          elevation={10}
          sx={{ borderRadius: 2, overflow: "hidden" }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              {t("settings.quick_actions_title")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {t("settings.quick_actions_desc")}
            </Typography>
          </Box>
          <List disablePadding>
            {ALL_QUICK_ACTIONS.map((key, index) => {
              const meta = ACTION_META[key];
              const enabled = visibleActions.includes(key);
              return (
                <ListItem
                  key={key}
                  divider={index < ALL_QUICK_ACTIONS.length - 1}
                  secondaryAction={
                    <Switch
                      checked={enabled}
                      onChange={() => toggleAction(key)}
                      color="primary"
                    />
                  }
                >
                  <ListItemIcon sx={{ fontSize: "1.6rem", minWidth: 44 }}>
                    {meta.icon}
                  </ListItemIcon>
                  <ListItemText primary={meta.label} />
                </ListItem>
              );
            })}
          </List>
        </Paper>

        <Paper
          elevation={10}
          sx={{ borderRadius: 2, overflow: "hidden", mt: 3 }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              {t("settings.notifications_title")}
            </Typography>
          </Box>
          <List disablePadding>
            <ListItem
              divider={hasToken && childList.length > 0}
              secondaryAction={
                <Switch
                  checked={hasToken}
                  onChange={handleToggleNotifications}
                  disabled={notifLoading}
                  color="primary"
                />
              }
            >
              <ListItemText
                primary={t("settings.push_notifications")}
                secondary={t("settings.push_notifications_desc")}
              />
            </ListItem>
            {hasToken && childList.length > 0 && (
              <>
                <ListItem sx={{ px: 2, pt: 1, pb: 0 }}>
                  <ListItemText
                    secondary={t("settings.receive_for")}
                    slotProps={{ secondary: { variant: "caption" } }}
                  />
                </ListItem>
                <Divider />
                {childList.map((child, index) => (
                  <ListItem
                    key={child.id}
                    disablePadding
                    divider={index < childList.length - 1}
                  >
                    <ListItemButton
                      onClick={() => handleToggleChild(child.id)}
                      dense
                    >
                      <Checkbox
                        checked={notificationChildren.includes(child.id)}
                        size="small"
                        disableRipple
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      <ListItemText primary={child.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </>
            )}
          </List>
        </Paper>

        <Paper
          elevation={10}
          sx={{ borderRadius: 2, overflow: "hidden", mt: 3 }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              {t("settings.children_title")}
            </Typography>
          </Box>
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate("/register-child")}>
                <ListItemIcon sx={{ minWidth: 44 }}>
                  <AddCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={t("settings.add_child")} />
              </ListItemButton>
            </ListItem>
          </List>
        </Paper>

        {currentChild && (
          <Paper
            elevation={10}
            sx={{ borderRadius: 2, overflow: "hidden", mt: 3 }}
          >
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight={600}
              >
                {currentChild.name.toUpperCase()}
              </Typography>
            </Box>
            <List disablePadding>
              <ListItem>
                <ListItemText primary={t("settings.gender")} />
                <ToggleButtonGroup
                  value={gender}
                  exclusive
                  onChange={handleGenderChange}
                  size="small"
                >
                  <ToggleButton value="boy">{t("settings.gender_boy")}</ToggleButton>
                  <ToggleButton value="girl">{t("settings.gender_girl")}</ToggleButton>
                </ToggleButtonGroup>
              </ListItem>
            </List>
          </Paper>
        )}

        <Paper
          elevation={10}
          sx={{ borderRadius: 2, overflow: "hidden", mt: 3 }}
        >
          <List disablePadding>
            <ListItem divider>
              <ListItemText primary={t("settings.language")} />
              <ToggleButtonGroup
                value={language}
                exclusive
                onChange={handleLanguageChange}
                size="small"
              >
                <ToggleButton value="da">{t("settings.language_da")}</ToggleButton>
                <ToggleButton value="en">{t("settings.language_en")}</ToggleButton>
              </ToggleButtonGroup>
            </ListItem>
            <ListItem
              secondaryAction={
                <Switch
                  checked={mode === "dark"}
                  onChange={toggleMode}
                  color="primary"
                />
              }
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                {mode === "dark" ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
              </ListItemIcon>
              <ListItemText primary={t("settings.dark_mode")} />
            </ListItem>
          </List>
        </Paper>
      </Container>
    </Box>
  );
};

export default SettingsPage;
